<template>
    <div class="deepseek-chat-container">
        <div class="chat-layout">
            <div class="chat-sidebar">
                <div class="sidebar-header">
                    <h2 class="sidebar-title">{{ title }}</h2>
                    <button class="new-chat-btn" @click="toggleNewChat">
                        <i class="el-icon-plus"></i>
                    </button>
                </div>
                <div class="sidebar-search">
                    <i class="el-icon-search"></i>
                    <input type="text" v-model="searchQuery" placeholder="搜索对话..." class="search-input">
                </div>
                <div class="conversation-list" v-loading="conversationsLoading">
                    <div 
                        v-for="(conv, index) in filteredConversations" 
                        :key="conv.id" 
                        class="conversation-item"
                        :class="{ 'is-active': currentConversationId === conv.id }"
                        @click="switchConversation(conv)"
                    >
                        <div class="conv-info">
                            <div class="conv-title">{{ conv.name || '新对话' }}</div>
                        </div>
                        <div class="conv-actions">
                            <button class="conv-menu" @click.stop="deleteConversation(conv)">
                                <i class="el-icon-delete"></i>
                            </button>
                        </div>
                    </div>
                    <div v-if="!conversationsLoading && conversations.length === 0" class="empty-conversations">
                        <i class="el-icon-chat-dot-round"></i>
                        <span>暂无对话</span>
                    </div>
                </div>
            </div>
            <div class="chat-main-area">
                <!-- <div class="main-header" v-if="messages.length > 0">
                    <div class="header-left">
                        <div class="header-avatar">
                            <i class="el-icon-chat-dot-round"></i>
                        </div>
                        <div class="header-info">
                            <h3 class="header-title">{{ currentConversation?.name || '飞凡智策' }}</h3>
                            <span class="header-subtitle">快速模式</span>
                        </div>
                    </div>
                    <div class="header-actions">
                        <el-tooltip content="分享" placement="top" effect="light">
                            <span class="header-action-btn"><i class="el-icon-share"></i></span>
                        </el-tooltip>
                        <el-tooltip content="更多" placement="top" effect="light">
                            <span class="header-action-btn"><i class="el-icon-more"></i></span>
                        </el-tooltip>
                    </div>
                </div> -->
                <div class="chat-main" ref="chatMainRef">
                    <div v-if="messages.length === 0" class="welcome-section">
                        <div class="welcome-avatar">
                            <i class="el-icon-chat-dot-round"></i>
                        </div>
                        <h1 class="welcome-title">{{ title }}</h1>
                        <p class="welcome-subtitle">有什么可以帮您的？</p>
                        <div class="suggestion-chips">
                            <div v-for="(suggestion, index) in suggestionWithHistory" :key="index" class="suggestion-chip" @click="useSuggestion(suggestion)">
                                <i :class="index < suggestions.length ? 'el-icon-question' : 'el-icon-time'"></i>
                                <span>{{ suggestion }}</span>
                            </div>
                        </div>
                    </div>
                    <div v-else class="messages-container" ref="messagesContainer" v-loading="messagesLoading">
                        <div v-for="(message, index) in messages" :key="message.id" class="message-item" :class="{'is-user': message.isUser}">
                            <div class="message-avatar" :class="{'is-user': message.isUser}">
                                <i v-if="message.isUser" class="el-icon-user"></i>
                                <i v-else class="el-icon-chat-dot-round"></i>
                            </div>
                            <div class="message-content">
                                <div class="message-header">
                                    <span class="message-role">{{ message.isUser ? '我' : title }}</span>
                                    <div class="message-actions" v-if="!message.isLoading">
                                        <el-tooltip content="复制" placement="top" effect="light">
                                            <span class="action-btn" @click="copyMessage(message)">
                                                <i class="el-icon-document-copy"></i>
                                            </span>
                                        </el-tooltip>
                                        <el-tooltip v-if="!message.isUser" content="重新生成" placement="top" effect="light">
                                            <span class="action-btn" @click="regenerateMessage(index)">
                                                <i class="el-icon-refresh"></i>
                                            </span>
                                        </el-tooltip>
                                    </div>
                                </div>
                                <div class="message-text">
                                    <div v-if="message.isLoading" class="typing-indicator">
                                        <span class="typing-dot"></span>
                                        <span class="typing-dot"></span>
                                        <span class="typing-dot"></span>
                                    </div>
                                    <div v-else-if="message.isGeneratingChart" class="chart-generating">
                                        <span class="generating-icon">📊</span>
                                        <span class="generating-text">正在生成图表分析...</span>
                                        <div class="typing-indicator inline">
                                            <span class="typing-dot"></span>
                                            <span class="typing-dot"></span>
                                            <span class="typing-dot"></span>
                                        </div>
                                    </div>
                                    <div v-else-if="message.isGeneratingText" class="text-generating">
                                        <span class="generating-icon">✍️</span>
                                        <span class="generating-text">正在生成中...</span>
                                        <div class="typing-indicator inline">
                                            <span class="typing-dot"></span>
                                            <span class="typing-dot"></span>
                                            <span class="typing-dot"></span>
                                        </div>
                                    </div>
                                    <div v-else>
                                        <div v-if="message.chartOption" class="chart-container" :id="'chart-' + message.id"></div>
                                        <div v-if="message.content" class="markdown-content" :class="{'is-streaming': message.isStreaming}" v-html="parseMarkdown(message.content)"></div>
                                    </div>
                                </div>
                                <div class="message-footer" v-if="!message.isUser && !message.isLoading">
                                    <span class="message-time">{{ message.time }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="chat-input-wrapper">
                    <div class="input-box">
                        <textarea 
                            v-model="inputMessage" 
                            placeholder="给飞凡智策发送消息" 
                            class="chat-textarea"
                            :disabled="!isCanSend"
                            @keydown.enter.exact="handleSend"
                            @input="autoResize"
                            ref="textareaRef"
                        ></textarea>
                        <div class="input-actions">
                            <span class="char-counter">{{ inputMessage.length }}/1000</span>
                            <button 
                                class="send-btn" 
                                :class="{'is-active': inputMessage.trim() && isCanSend}"
                                :disabled="!inputMessage.trim() || !isCanSend"
                                @click="handleSend"
                                @keydown.enter.exact="handleSend"
                            >
                                <i class="el-icon-top"></i>
                            </button>
                        </div>
                    </div>
                    <div class="input-footer">
                        <span>内容由 AI 生成，请仔细甄别</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as echarts from 'echarts';

export default {
    name: 'databaseQuery',
    props: {
        databaseTable: {
            type: String,
            default: ''
        },
        // 欢迎页的智能建议问题
        suggestions: {
            type: Array,
            default: () => []
        },
        title: {
            type: String,
            default: ''
        },
    },
    data() {
        return {
            // 消息列表
            messages: [],
            // 输入框内容
            inputMessage: '',
            // 是否可以发送消息（防止重复提交）
            isCanSend: true,

            // Markdown 解析器实例
            md: null,
            // 当前会话 ID（用于发送消息）
            conversationId: '',
            // 当前选中的会话 ID（用于 UI 高亮）
            currentConversationId: '',
            // 当前用户 ID（用于发送消息）
            userId: '',
            // 搜索框内容
            searchQuery: '',
            // 会话列表
            conversations: [],
            // 会话列表加载状态
            conversationsLoading: false,
            // 消息历史加载状态
            messagesLoading: false,
            // ECharts 图表实例映射
            chartInstances: {},
            BASE_URL: window.global_config.BASE_URL,
            // resize 事件监听器
            chartResizeListeners: []
        }
    },
    computed: {
        // 获取当前选中的会话对象
        currentConversation() {
            return this.conversations.find(c => c.id === this.currentConversationId) || null;
        },
        // 根据搜索关键词过滤会话列表
        filteredConversations() {
            if (!this.searchQuery) return this.conversations;
            const query = this.searchQuery.toLowerCase();
            return this.conversations.filter(c => 
                (c.name && c.name.toLowerCase().includes(query)) || 
                (c.preview && c.preview.toLowerCase().includes(query))
            );
        },
        // 组合建议问题和历史消息
        suggestionWithHistory() {
            const result = [...this.suggestions];
            // 添加最近两条历史消息
            if (this.conversations.length > 0) {
                const recentConversations = this.conversations.slice(0, 2);
                recentConversations.forEach(conv => {
                    if ((conv.name && result.indexOf(conv.name)) === -1 && result.length < 5) {
                        result.push(conv.name);
                    }
                });
            }
            return result;
        }
    },
    mounted() {
        // 初始化 Markdown 解析器
        this.getUserInfo();
        if (window.markdownit) {
            this.md = window.markdownit({
                html: true,
                linkify: true,
                breaks: true
            });
        }
        // 页面加载时获取会话列表
        this.loadConversations();
    },
    beforeDestroy() {
        this.destroyAllCharts();
    },
    methods: {
        // 获取用户信息
        getUserInfo() {
            var info = sessionStorage.getItem("userInfo");
            if (info) {
                const opmId = JSON.parse(info).opmId;
                // 获取当前路由地址
                const currentRoute = this.$route;
                console.log(currentRoute.path);
                if (currentRoute.path) {
                    this.userId = this.BASE_URL + '-' + currentRoute.path + '-' + opmId;
                } else {
                    this.userId = this.BASE_URL + '-' +opmId;
                }
            }
        },
        // 获取 API 请求头（包含认证信息）
        getHeaders() {
            return {
                "Authorization": `Bearer ${window.global_config.AI_API.DATABASE_API_KEY}`,
                'Content-Type': 'application/json'
            };
        },
        // 获取 API 基础 URL
        getBaseUrl() {
            return `${window.global_config.AI_CHAT_URL}/v1`;
        },
        // 获取数据库连接字符串
        getDB_URI() {
            return window.global_config.DB_URI;
        },
        // 从 Dify 获取会话列表
        async loadConversations() {
            this.conversationsLoading = true;
            try {
                const params = new URLSearchParams({
                    user: this.userId,
                    limit: '50'
                });
                const response = await fetch(`${this.getBaseUrl()}/conversations?${params}`, {
                    method: 'GET',
                    headers: this.getHeaders()
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const data = await response.json();
                this.conversations = data.data || [];
                
                // 如果有会话且当前没有选中，默认选中第一个
                if (this.conversations.length > 0 && !this.currentConversationId) {
                    this.switchConversation(this.conversations[0]);
                }
            } catch (error) {
                console.error('获取会话列表失败:', error);
                this.$message.error('获取会话列表失败');
            } finally {
                this.conversationsLoading = false;
            }
        },
        // 获取指定会话的消息历史
        async loadConversationMessages(conversationId) {
            if (!conversationId) {
                this.messages = [];
                return;
            }
            
            this.messagesLoading = true;
            try {
                const params = new URLSearchParams({
                    user: this.userId,
                    conversation_id: conversationId,
                    limit: '100'
                });
                const response = await fetch(`${this.getBaseUrl()}/messages?${params}`, {
                    method: 'GET',
                    headers: this.getHeaders()
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const data = await response.json();
                const apiMessages = data.data || [];
                
                // 按时间排序
                apiMessages.sort((a, b) => a.created_at - b.created_at);
                
                // 将 Dify 返回的数据转换为前端需要的格式
                const formattedMessages = [];
                apiMessages.forEach(msg => {
                    // 用户消息
                    if (msg.query) {
                        formattedMessages.push({
                            id: msg.id + '-user',
                            content: msg.query,
                            isUser: true,
                            time: this.formatTime(msg.created_at * 1000)
                        });
                    }
                    // AI 回复
                    if (msg.answer) {
                        const parsed = this.parseResponseContent(msg.answer);
                        formattedMessages.push({
                            id: msg.id + '-ai',
                            content: parsed.text,
                            chartOption: parsed.chartOption,
                            isUser: false,
                            isLoading: false,
                            time: this.formatTime(msg.created_at * 1000)
                        });
                    }
                });
                
                this.messages = formattedMessages;
                
                this.$nextTick(() => {
                    formattedMessages.forEach(msg => {
                        if (msg.chartOption && !msg.isUser) {
                            this.initChart(msg.id, msg.chartOption);
                        }
                    });
                });
            } catch (error) {
                console.error('获取消息历史失败:', error);
                this.$message.error('获取消息历史失败');
            } finally {
                this.messagesLoading = false;
            }
        },
        // 切换到指定会话
        async switchConversation(conversation) {
            this.destroyAllCharts();
            this.currentConversationId = conversation.id;
            this.conversationId = conversation.id;
            await this.loadConversationMessages(conversation.id);
            this.$nextTick(() => {
                this.scrollToBottom();
            });
        },
        // 删除指定会话
        async deleteConversation(conversation) {
            this.$confirm('确定要删除这个对话吗？', '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                try {
                    const response = await fetch(`${this.getBaseUrl()}/conversations/${conversation.id}`, {
                        method: 'DELETE',
                        headers: this.getHeaders(),
                        body: JSON.stringify({
                            user: this.userId
                        })
                    });
                    
                    if (!response.ok) {
                        const errorText = await response.text().catch(() => '');
                        throw new Error(`HTTP error! status: ${response.status}, ${errorText}`);
                    }
                    
                    this.destroyAllCharts();
                    
                    const index = this.conversations.findIndex(c => c.id === conversation.id);
                    if (index !== -1) {
                        this.conversations.splice(index, 1);
                    }
                    
                    if (this.currentConversationId === conversation.id) {
                        if (this.conversations.length > 0) {
                            await this.switchConversation(this.conversations[0]);
                        } else {
                            this.currentConversationId = '';
                            this.conversationId = '';
                            this.messages = [];
                            this.inputMessage = '';
                        }
                    }
                    
                    this.$message.success('删除成功');
                } catch (error) {
                    console.error('删除对话失败:', error);
                    this.$message.error('删除失败：' + error.message);
                }
            }).catch(() => {});
        },
        // 开始新对话（清空当前状态）
        toggleNewChat() {
            this.destroyAllCharts();
            this.currentConversationId = '';
            this.conversationId = '';
            this.messages = [];
            this.inputMessage = '';
            this.$nextTick(() => {
                this.autoResize();
            });
        },
        // 自动调整输入框高度
        autoResize() {
            const textarea = this.$refs.textareaRef;
            if (textarea) {
                textarea.style.height = 'auto';
                const newHeight = Math.min(textarea.scrollHeight, 200);
                textarea.style.height = newHeight + 'px';
            }
        },
        // 优化图表配置，对所有图表类型生效
        optimizeChartOption(chartOption) {
            if (!chartOption) return chartOption;
            
            const option = JSON.parse(JSON.stringify(chartOption));
            
            if (option.title) {
                option.title.top = '5%';
                option.title.left = 'center';
                option.title.textStyle = option.title.textStyle || {};
                option.title.textStyle.fontSize = option.title.textStyle.fontSize || 14;
                option.title.textStyle.fontWeight = 'bold';
            }
            
            if (option.grid) {
                if (!option.grid.containLabel) {
                    option.grid.containLabel = true;
                }
            }
            
            const truncateText = (text, maxLength = 10) => {
                if (!text || typeof text !== 'string') return text;
                if (text.length <= maxLength) return text;
                return text.substring(0, maxLength) + '...';
            };
            
            if (option.xAxis) {
                if (!option.xAxis.axisLabel) {
                    option.xAxis.axisLabel = {};
                }
                const originalFormatter = option.xAxis.axisLabel.formatter;
                option.xAxis.axisLabel.formatter = function(value) {
                    if (typeof originalFormatter === 'function') {
                        return originalFormatter(value);
                    }
                    return truncateText(value, 10);
                };
            }
            
            if (option.yAxis) {
                if (!option.yAxis.axisLabel) {
                    option.yAxis.axisLabel = {};
                }
                const originalFormatter = option.yAxis.axisLabel.formatter;
                option.yAxis.axisLabel.formatter = function(value) {
                    if (typeof originalFormatter === 'function') {
                        return originalFormatter(value);
                    }
                    return truncateText(value, 10);
                };
            }
            
            if (option.legend) {
                const originalFormatter = option.legend.formatter;
                option.legend.formatter = function(name) {
                    if (typeof originalFormatter === 'function') {
                        return originalFormatter(name);
                    }
                    return truncateText(name, 8);
                };
            }
            
            if (option.series && option.series.length > 0) {
                option.series.forEach(series => {
                    if (series.label) {
                        const originalFormatter = series.label.formatter;
                        series.label.formatter = function(params) {
                            if (typeof originalFormatter === 'function') {
                                return originalFormatter(params);
                            }
                            if (params.name) {
                                return truncateText(params.name, 99);
                            }
                            return params.value || '';
                        };
                    }
                    
                    if (series.type === 'pie' && series.data) {
                        series.data.forEach(item => {
                            if (item.label && !item.label.formatter) {
                                item.label.formatter = function(params) {
                                    return truncateText(params.name, 8);
                                };
                            }
                        });
                    }
                });
            }
            
            return option;
        },
        // 解析响应内容，检测是否包含图表数据
        parseResponseContent(content) {
            if (!content) {
                return { text: '', chartOption: null };
            }
            
            const jsonRegex = /\{[\s\S]*"success"[\s\S]*\}/;
            const jsonMatch = content.match(jsonRegex);
            
            if (jsonMatch) {
                try {
                    const jsonData = JSON.parse(jsonMatch[0]);
                    if (jsonData.success) {
                        return {
                            text: jsonData.analysis_text || '',
                            chartOption: jsonData.chart_option || null
                        };
                    }
                } catch (e) {
                    console.error('解析 JSON 失败:', e);
                }
            }
            
            const echartsRegex = /```echarts\s*([\s\S]*?)```/i;
            const match = content.match(echartsRegex);
            
            if (match) {
                try {
                    const chartOption = JSON.parse(match[1]);
                    const text = content.replace(echartsRegex, '').trim();
                    return {
                        text: text,
                        chartOption: chartOption
                    };
                } catch (e) {
                    console.error('解析 ECharts 配置失败:', e);
                }
            }
            
            return { text: content, chartOption: null };
        },
        // 从流式内容中提取可显示的文本（支持实时渲染）
        extractStreamingText(content) {
            if (!content) return '';

            const trimmed = content.trimStart();
            // 检测是否为 JSON 格式（以 { 开头，或包含 success/analysis_text 字段）
            const hasJsonMarker = trimmed.startsWith('{') ||
                content.indexOf('"success"') !== -1 ||
                content.indexOf('"analysis_text":') !== -1;
            const marker = '"analysis_text":';
            const markerIndex = content.indexOf(marker);

            if (hasJsonMarker) {
                if (markerIndex !== -1) {
                    // analysis_text 字段已开始传输，提取文本
                    return this.extractPartialJsonString(content, markerIndex + marker.length);
                }
                // JSON 传输中但 analysis_text 字段还未出现，不显示 JSON 代码
                return '';
            }

            // 纯文本/echarts 格式：移除 echarts 代码块后返回
            let text = content;
            const echartsStart = text.indexOf('```echarts');
            if (echartsStart !== -1) {
                text = text.substring(0, echartsStart);
            }
            return text;
        },
        // 从部分 JSON 中提取字符串值（处理转义字符，支持流式传输不完整的 JSON）
        extractPartialJsonString(content, startPos) {
            let start = startPos;
            // 跳过空白字符
            while (start < content.length && /\s/.test(content[start])) {
                start++;
            }
            if (start >= content.length || content[start] !== '"') return '';
            start++; // 跳过开头的引号

            let result = '';
            let i = start;
            while (i < content.length) {
                const ch = content[i];
                if (ch === '\\') {
                    if (i + 1 >= content.length) return result; // 转义不完整，等待更多数据
                    const next = content[i + 1];
                    switch (next) {
                        case 'n': result += '\n'; break;
                        case 't': result += '\t'; break;
                        case 'r': result += '\r'; break;
                        case '"': result += '"'; break;
                        case '\\': result += '\\'; break;
                        case '/': result += '/'; break;
                        case 'b': result += '\b'; break;
                        case 'f': result += '\f'; break;
                        case 'u':
                            if (i + 5 < content.length) {
                                const hex = content.substring(i + 2, i + 6);
                                result += String.fromCharCode(parseInt(hex, 16));
                                i += 6;
                                continue;
                            }
                            return result; // Unicode 转义不完整，等待更多数据
                        default: result += next;
                    }
                    i += 2;
                } else if (ch === '"') {
                    return result; // 字符串结束（完整）
                } else {
                    result += ch;
                    i++;
                }
            }
            return result; // 字符串未结束（流式传输中）
        },
        // 检测内容中是否包含图表数据
        detectChartInContent(content) {
            if (!content) return false;

            if (content.includes('```echarts')) {
                return true;
            }

            // 流式传输中：检测到 chart_option 字段即认为包含图表
            if (content.includes('"chart_option"')) {
                return true;
            }

            // 完整 JSON 检测
            const jsonRegex = /\{[\s\S]*"success"[\s\S]*\}/;
            const jsonMatch = content.match(jsonRegex);

            if (jsonMatch) {
                try {
                    const jsonData = JSON.parse(jsonMatch[0]);
                    if (jsonData.success && jsonData.chart_option) {
                        return true;
                    }
                } catch (e) {
                    // JSON 不完整，可能正在流式传输中
                }
            }

            return false;
        },
        // 初始化 ECharts 图表
        initChart(messageId, chartOption) {
            this.$nextTick(() => {
                const chartDom = document.getElementById('chart-' + messageId);
                if (!chartDom) return;
                
                if (this.chartInstances[messageId]) {
                    this.chartInstances[messageId].dispose();
                }
                
                const optimizedOption = this.optimizeChartOption(chartOption);
                const myChart = echarts.init(chartDom);
                myChart.setOption(optimizedOption);
                this.chartInstances[messageId] = myChart;
                
                const resizeHandler = () => {
                    myChart.resize();
                };
                window.addEventListener('resize', resizeHandler);
                this.chartResizeListeners.push(resizeHandler);
            });
        },
        // 销毁所有图表实例
        destroyAllCharts() {
            this.chartResizeListeners.forEach(listener => {
                window.removeEventListener('resize', listener);
            });
            this.chartResizeListeners = [];
            Object.keys(this.chartInstances).forEach(id => {
                if (this.chartInstances[id]) {
                    this.chartInstances[id].dispose();
                }
            });
            this.chartInstances = {};
        },
        // 使用欢迎页的智能建议问题
        useSuggestion(suggestion) {
            this.inputMessage = suggestion;
            this.$nextTick(() => {
                this.autoResize();
                // 聚焦到输入框
                if (this.$refs.textareaRef) {
                    this.$refs.textareaRef.focus();
                }
            });
        },
        // 解析 Markdown 文本为 HTML
        parseMarkdown(text) {
            if (!text) return '';
            if (this.md) {
                return this.md.render(text);
            }
            return text.replace(/\n/g, '<br>');
        },
        // 格式化时间戳为 HH:mm
        formatTime(timestamp) {
            const d = new Date(timestamp);
            const hours = d.getHours().toString().padStart(2, '0');
            const minutes = d.getMinutes().toString().padStart(2, '0');
            return `${hours}:${minutes}`;
        },
        // 复制消息内容（优先使用现代 Clipboard API）
        copyMessage(message) {
            const text = message.content;
            if (navigator.clipboard) {
                navigator.clipboard.writeText(text).then(() => {
                    this.$message.success('已复制到剪贴板');
                }).catch(() => {
                    this.fallbackCopy(text);
                });
            } else {
                this.fallbackCopy(text);
            }
        },
        // 复制消息内容的降级方案（使用 execCommand）
        fallbackCopy(text) {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.left = '-9999px';
            document.body.appendChild(textarea);
            textarea.select();
            try {
                document.execCommand('copy');
                this.$message.success('已复制到剪贴板');
            } catch {
                this.$message.error('复制失败，请手动复制');
            }
            document.body.removeChild(textarea);
        },
        // 重新生成 AI 回复
        async regenerateMessage(index) {
            // 找到对应的用户消息
            let userMessageIndex = -1;
            for (let i = index - 1; i >= 0; i--) {
                if (this.messages[i].isUser) {
                    userMessageIndex = i;
                    break;
                }
            }
            
            if (userMessageIndex === -1) {
                this.$message.warning('未找到对应的用户消息');
                return;
            }
            
            // 截断到用户消息，重新发送
            const userMessage = this.messages[userMessageIndex];
            this.messages = this.messages.slice(0, userMessageIndex + 1);
            await this.sendMessage(userMessage.content, false);
        },
        // 发送按钮点击事件
        async handleSend() {
            const message = this.inputMessage.trim();
            if (!message || !this.isCanSend) return;
            if (message.length > 1000) {
                this.$message.warning('消息内容不能超过1000字');
                return;
            }
            await this.sendMessage(message, true);
        },
        // 发送消息到 Dify API（支持流式响应）
        async sendMessage(query, addToHistory = true) {
            // 添加用户消息到列表
            if (addToHistory) {
                const userMessage = {
                    id: Date.now(),
                    content: query,
                    isUser: true,
                    time: this.formatTime(new Date())
                };
                this.messages.push(userMessage);
                
                // 更新会话预览
                if (this.currentConversation) {
                    this.currentConversation.preview = query.length > 30 ? query.substring(0, 30) + '...' : query;
                }
            }
            
            // 禁用发送按钮，清空输入框
            this.isCanSend = false;
            this.inputMessage = '';
            this.$nextTick(() => {
                this.autoResize();
                this.scrollToBottom();
            });
            
            // 添加 AI 正在加载的消息
            const aiMessageId = Date.now() + 1;
            const aiMessage = {
                id: aiMessageId,
                content: '',
                isUser: false,
                isLoading: true,
                isGeneratingChart: false,
                isGeneratingText: false,
                isStreaming: false,
                time: this.formatTime(new Date())
            };
            this.messages.push(aiMessage);
            
            this.$nextTick(() => {
                this.scrollToBottom();
            });
            
            try {
                // 调用 Dify 聊天接口（流式响应）
                const response = await fetch(`${this.getBaseUrl()}/chat-messages`, {
                    method: 'POST',
                    headers: this.getHeaders(),
                    body: JSON.stringify({
                        query: query,
                        response_mode: "streaming",
                        user: this.userId,
                        files: [],
                        inputs: {
                            database_table: this.databaseTable,
                            DB_URI: this.getDB_URI()
                        },
                        conversation_id: this.conversationId,
                        auto_generate_name: true
                    })
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                // 处理 SSE 流式响应
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                let fullContent = '';
                let newConversationId = '';
                let newConversationName = '';
                let hasChartBlock = false;
                
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) {
                        console.log('流式数据接收完成');
                        break;
                    }
                    
                    // 解析 SSE 数据
                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop();
                    
                    lines.forEach(line => {
                        if (line.startsWith('data:')) {
                            const data = line.substring(5).trim();
                            if (data) {
                                try {
                                    const parsedData = JSON.parse(data);
                                    
                                    // 保存新会话 ID
                                    if (parsedData.conversation_id && !newConversationId) {
                                        newConversationId = parsedData.conversation_id;
                                        this.conversationId = parsedData.conversation_id;
                                        this.currentConversationId = parsedData.conversation_id;
                                    }
                                    
                                    // 会话名称更新事件
                                    if (parsedData.event === 'conversation_name_replace') {
                                        newConversationName = parsedData.name || newConversationName;
                                    }
                                    
                                    // 消息内容事件（流式返回）
                                    if (parsedData.event === 'message') {
                                        const index = this.messages.findIndex(m => m.id === aiMessageId);
                                        if (index !== -1) {
                                            if (this.messages[index].isLoading) {
                                                this.$set(this.messages[index], 'isLoading', false);
                                            }
                                            fullContent += parsedData.answer || '';

                                            const hasChart = this.detectChartInContent(fullContent);
                                            // 流式输出：优先显示文本，没有文本时显示生成状态
                                            const streamingText = this.extractStreamingText(fullContent);
                                            if (streamingText) {
                                                // 有可显示的文本，流式显示（打字机效果）
                                                this.$set(this.messages[index], 'isGeneratingChart', false);
                                                this.$set(this.messages[index], 'isGeneratingText', false);
                                                this.$set(this.messages[index], 'content', streamingText);
                                                this.$set(this.messages[index], 'isStreaming', true);
                                            } else {
                                                // 没有可显示的文本，显示生成状态
                                                this.$set(this.messages[index], 'isStreaming', false);
                                                if (hasChart) {
                                                    this.$set(this.messages[index], 'isGeneratingChart', true);
                                                    this.$set(this.messages[index], 'isGeneratingText', false);
                                                } else {
                                                    this.$set(this.messages[index], 'isGeneratingChart', false);
                                                    this.$set(this.messages[index], 'isGeneratingText', true);
                                                }
                                            }

                                            this.scrollToBottom();
                                        }
                                    } else if (parsedData.event === 'message_end' || parsedData.event === 'workflow_finished') {
                                        const index = this.messages.findIndex(m => m.id === aiMessageId);
                                        if (index !== -1) {
                                            this.$set(this.messages[index], 'isLoading', false);
                                            
                                            // 在消息结束时解析并设置内容
                                            const parsed = this.parseResponseContent(fullContent);
                                            this.$set(this.messages[index], 'content', parsed.text);
                                            this.$set(this.messages[index], 'chartOption', parsed.chartOption);
                                            this.$set(this.messages[index], 'isGeneratingChart', false);
                                            this.$set(this.messages[index], 'isGeneratingText', false);
                                            this.$set(this.messages[index], 'isStreaming', false);
                                            
                                            if (parsed.chartOption) {
                                                this.initChart(aiMessageId, parsed.chartOption);
                                            }
                                            
                                            this.scrollToBottom();
                                        }
                                    }
                                } catch (e) {
                                    console.error('解析数据失败:', e);
                                }
                            }
                        }
                    });
                }
                
                if (newConversationId) {
                    await this.loadConversations();
                }
                
                // 内容解析已经在 message_end 或 workflow_finished 事件中处理
            } catch (error) {
                console.error('请求错误:', error);
                const index = this.messages.findIndex(m => m.id === aiMessageId);
                if (index !== -1) {
                    this.$set(this.messages[index], 'isLoading', false);
                    this.$set(this.messages[index], 'isGeneratingChart', false);
                    this.$set(this.messages[index], 'isGeneratingText', false);
                    this.$set(this.messages[index], 'isStreaming', false);
                    this.messages[index].content = '抱歉，请求失败，请稍后重试。\n\n错误信息：' + error.message;
                }
                this.$message.error('请求失败，请稍后重试');
            }
            
            this.isCanSend = true;
            this.$nextTick(() => {
                this.scrollToBottom();
            });
        },
        scrollToBottom() {
            this.$nextTick(() => {
                const container = this.$refs.messagesContainer;
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
            });
        }
    }
}
</script>

<style scoped>
.deepseek-chat-container {
    width: 100%;
    height: 100%;
    display: flex;
    background: #ffffff;
}

.chat-layout {
    display: flex;
    width: 100%;
    height: 100%;
}

.chat-sidebar {
    width: 280px;
    background: #f9fafb;
    border-right: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
}

.sidebar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid #e0e0e0;
}

.sidebar-title {
    font-size: 16px;
    font-weight: 600;
    color: #333333;
    margin: 0;
}

.new-chat-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    background: #4a90e2;
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.new-chat-btn:hover {
    background: #3a80d2;
}

.sidebar-search {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-bottom: 1px solid #e0e0e0;
}

.sidebar-search i {
    color: #999999;
}

.search-input {
    flex: 1;
    height: 28px;
    padding: 0 12px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    font-size: 14px;
    outline: none;
    transition: all 0.2s ease;
}

.search-input:focus {
    border-color: #4a90e2;
}

.search-input::placeholder {
    color: #999999;
}

.conversation-list {
    flex: 1;
    overflow-y: auto;
    padding: 4px 8px;
}

.conversation-list::-webkit-scrollbar {
    width: 4px;
}

.conversation-list::-webkit-scrollbar-track {
    background: transparent;
}

.conversation-list::-webkit-scrollbar-thumb {
    background: #e0e0e0;
    border-radius: 2px;
}

.conversation-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.conversation-item:hover {
    background: #f1f3f5;
}

.conversation-item.is-active {
    background: #e8f3ff;
}

.conv-info {
    flex: 1;
    overflow: hidden;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;
}

.conv-title {
    font-size: 14px;
    font-weight: 400;
    color: #333333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.conversation-item.is-active .conv-title {
    color: #4a90e2;
}

.conv-tag {
    background: #e8f3ff;
    color: #4a90e2;
    font-size: 12px;
    padding: 2px 8px;
    border-radius: 4px;
    flex-shrink: 0;
}

.conv-actions {
    opacity: 0;
    transition: opacity 0.2s ease;
}

.conversation-item:hover .conv-actions {
    opacity: 1;
}

.conv-menu {
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 50%;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999999;
}

.conv-menu:hover {
    background: #e5e8eb;
}

.empty-conversations {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: #999999;
}

.empty-conversations i {
    font-size: 40px;
    margin-bottom: 12px;
    opacity: 0.5;
}

.chat-main-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.main-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: #fff;
    border-bottom: 1px solid #e0e0e0;
    flex-shrink: 0;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 12px;
}

.header-avatar {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, #4a90e2 0%, #3a80d2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
}

.header-avatar i {
    font-size: 18px;
    color: #fff;
}

.header-info {
    display: flex;
    flex-direction: column;
}

.header-title {
    font-size: 14px;
    font-weight: 600;
    color: #333333;
    margin: 0;
}

.header-subtitle {
    font-size: 12px;
    color: #999999;
}

.header-actions {
    display: flex;
    gap: 8px;
}

.header-action-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #999999;
    transition: all 0.2s ease;
}

.header-action-btn:hover {
    background: #f0f0f0;
    color: #4a90e2;
}

.chat-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: linear-gradient(180deg, #f5f5f5 0%, #ffffff 100%);
}

.welcome-section {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
}

.welcome-avatar {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #4a90e2 0%, #3a80d2 100%);
    border-radius: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
    box-shadow: 0 8px 24px rgba(74, 144, 226, 0.3);
}

.welcome-avatar i {
    font-size: 40px;
    color: #fff;
}

.welcome-title {
    font-size: 32px;
    font-weight: 600;
    color: #333333;
    margin: 0 0 12px 0;
}

.welcome-subtitle {
    font-size: 16px;
    color: #666666;
    margin: 0 0 32px 0;
}

.suggestion-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: center;
    max-width: 600px;
}

.suggestion-chip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 14px;
    color: #666666;
}

.suggestion-chip:hover {
    background: #f5f7fa;
    border-color: #4a90e2;
    color: #4a90e2;
}

.suggestion-chip i {
    color: #999999;
}

.suggestion-chip:hover i {
    color: #4a90e2;
}

.messages-container {
    flex: 1;
    overflow-y: auto;
    padding: 24px 20px 0;
}

.messages-container::-webkit-scrollbar {
    width: 6px;
}

.messages-container::-webkit-scrollbar-track {
    background: transparent;
}

.messages-container::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.messages-container::-webkit-scrollbar-thumb:hover {
    background: #a1a1a1;
}

.message-item {
    display: flex;
    gap: 16px;
    margin-bottom: 32px;
}

.message-item.is-user {
    flex-direction: row-reverse;
}

.message-avatar {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: linear-gradient(135deg, #4a90e2 0%, #3a80d2 100%);
}

.message-avatar.is-user {
    background: #e8f3ff;
}

.message-avatar i {
    font-size: 20px;
    color: #fff;
}

.message-avatar.is-user i {
    color: #4a90e2;
}

.message-content {
    max-width: 70%;
    display: flex;
    flex-direction: column;
}

.message-item.is-user .message-content {
    align-items: flex-end;
}

.message-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
}

.message-item.is-user .message-header {
    flex-direction: row-reverse;
}

.message-role {
    font-size: 14px;
    font-weight: 500;
    color: #999999;
}

.message-actions {
    display: flex;
    gap: 8px;
    opacity: 0;
    transition: opacity 0.2s ease;
}

.message-item:hover .message-actions {
    opacity: 1;
}

.action-btn {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #999999;
    transition: all 0.2s ease;
}

.action-btn:hover {
    background: #f0f0f0;
    color: #4a90e2;
}

.message-text {
    position: relative;
}

.message-item.is-user .message-text {
    background: #edf3fe;
    color: #0f1115;
    border-radius: 12px 12px 4px 12px;
    /* padding: 12px 16px; */
}

.message-item:not(.is-user) .message-text {
    color: #333333;
}

.typing-indicator {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 12px 16px;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 12px 12px 12px 4px;
}

.typing-dot {
    width: 8px;
    height: 8px;
    background: #4a90e2;
    border-radius: 50%;
    animation: typing 1.4s ease-in-out infinite;
}

.typing-dot:nth-child(2) {
    animation-delay: 0.2s;
}

.typing-dot:nth-child(3) {
    animation-delay: 0.4s;
}

.chart-container {
    width: 100%;
    height: 400px;
    margin-bottom: 12px;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 12px 12px 12px 4px;
}

.chart-generating {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px 20px;
    background: #f5f5f5;
    border-radius: 12px 12px 12px 4px;
    color: #666666;
}

.chart-generating .generating-icon {
    font-size: 20px;
}

.chart-generating .generating-text {
    font-size: 14px;
}

.chart-generating .typing-indicator.inline {
    display: inline-flex;
    margin-left: 4px;
    transform: none;
}

.chart-generating .typing-indicator.inline .typing-dot {
    width: 4px;
    height: 4px;
    margin: 0 2px;
}

.text-generating {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px 20px;
    background: #f5f5f5;
    border-radius: 12px 12px 12px 4px;
    color: #666666;
}

.text-generating .generating-icon {
    font-size: 20px;
}

.text-generating .generating-text {
    font-size: 14px;
}

.text-generating .typing-indicator.inline {
    display: inline-flex;
    margin-left: 4px;
    transform: none;
}

.text-generating .typing-indicator.inline .typing-dot {
    width: 4px;
    height: 4px;
    margin: 0 2px;
}

@keyframes typing {
    0%, 60%, 100% {
        transform: translateY(0);
        opacity: 0.4;
    }
    30% {
        transform: translateY(-6px);
        opacity: 1;
    }
}

.markdown-content {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 12px 12px 12px 4px;
    padding: 12px 16px;
    line-height: 1.75;
}

.message-item.is-user .message-text {
    border: none;
}

.message-item.is-user .markdown-content {
    background: #edf3fe;
    color: #0f1115;
    border: none;
}

.message-item.is-user .markdown-content ::v-deep p {
    color: #0f1115;
}

.message-footer {
    margin-top: 8px;
}

.message-time {
    font-size: 12px;
    color: #c1c1c1;
}

.chat-input-wrapper {
    padding: 4px 20px 4px;
    background: #fff;
    border-top: 1px solid #e0e0e0;
    flex-shrink: 0;
}

.input-box {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 16px;
    padding: 12px 16px;
    transition: all 0.2s ease;
}

.input-box:focus-within {
    border-color: #4a90e2;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
}

.chat-textarea {
    width: 100%;
    min-height: 24px;
    max-height: 200px;
    border: none;
    outline: none;
    resize: none;
    font-size: 15px;
    line-height: 1.6;
    color: #333333;
    background: transparent;
}

.chat-textarea::placeholder {
    color: #999999;
}

.input-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
}

.char-counter {
    font-size: 12px;
    color: #c1c1c1;
}

.send-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: #e0e0e0;
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.send-btn.is-active {
    background: linear-gradient(135deg, #4a90e2 0%, #3a80d2 100%);
}

.send-btn.is-active:hover {
    transform: scale(1.05);
}

.send-btn:disabled {
    cursor: not-allowed;
}

.send-btn i {
    font-size: 16px;
}

.input-buttons {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-top: 12px;
}

.input-feature-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border: 1px solid #e0e0e0;
    border-radius: 20px;
    background: #fff;
    cursor: pointer;
    font-size: 13px;
    color: #666666;
    transition: all 0.2s ease;
}

.input-feature-btn:hover {
    background: #f0f0f0;
    border-color: #4a90e2;
    color: #4a90e2;
}

.input-feature-btn i {
    font-size: 14px;
}

.input-footer {
    text-align: center;
}

.input-footer span {
    font-size: 12px;
    color: #c1c1c1;
}

.markdown-content ::v-deep h1,
.markdown-content ::v-deep h2,
.markdown-content ::v-deep h3,
.markdown-content ::v-deep h4,
.markdown-content ::v-deep h5,
.markdown-content ::v-deep h6 {
    margin: 16px 0 8px 0;
    font-weight: 600;
    color: #333333;
}

.markdown-content ::v-deep h1 {
    font-size: 24px;
}

.markdown-content ::v-deep h2 {
    font-size: 20px;
}

.markdown-content ::v-deep h3 {
    font-size: 18px;
}

.markdown-content ::v-deep h4 {
    font-size: 16px;
}

.markdown-content ::v-deep p {
    margin: 0px 0;
}

.markdown-content ::v-deep ul,
.markdown-content ::v-deep ol {
    margin: 8px 0;
    padding-left: 24px;
}

.markdown-content ::v-deep ul {
    list-style: disc;
}

.markdown-content ::v-deep ol {
    list-style: decimal;
}

.markdown-content ::v-deep li {
    margin: 4px 0;
}

.markdown-content ::v-deep li > ul,
.markdown-content ::v-deep li > ol {
    margin: 4px 0;
}

.markdown-content ::v-deep code {
    background: #f7f8fa;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: "Consolas", "Monaco", monospace;
    font-size: 13px;
    color: #f53f3f;
}

.markdown-content ::v-deep pre {
    background: #1e1e1e;
    padding: 12px 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 8px 0;
}

.markdown-content ::v-deep pre code {
    background: transparent;
    color: #d4d4d4;
    padding: 0;
}

.markdown-content ::v-deep blockquote {
    border-left: 4px solid #4a90e2;
    padding: 8px 12px;
    margin: 8px 0;
    background: #f7f8fa;
    color: #666666;
}

.markdown-content ::v-deep a {
    color: #4a90e2;
    text-decoration: none;
}

.markdown-content ::v-deep a:hover {
    text-decoration: underline;
}

.markdown-content ::v-deep table {
    border-collapse: collapse;
    width: 100%;
    margin: 8px 0;
}

.markdown-content ::v-deep th,
.markdown-content ::v-deep td {
    border: 1px solid #e0e0e0;
    padding: 8px 12px;
    text-align: left;
}

.markdown-content ::v-deep th {
    background: #f7f8fa;
    font-weight: 500;
}

.markdown-content ::v-deep hr {
    border: none;
    border-top: 1px solid #e0e0e0;
    margin: 16px 0;
}

/* 流式生成时的闪烁光标 */
.markdown-content.is-streaming ::v-deep > p:last-child::after,
.markdown-content.is-streaming ::v-deep > ul:last-child > li:last-child::after,
.markdown-content.is-streaming ::v-deep > ol:last-child > li:last-child::after {
    content: '';
    display: inline-block;
    width: 7px;
    height: 1em;
    background: #4a90e2;
    margin-left: 3px;
    vertical-align: text-bottom;
    border-radius: 1px;
    animation: cursor-blink 1s steps(2, start) infinite;
}

@keyframes cursor-blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
}
</style>
